import * as THREE from 'three';

import { formatDistanceForHud } from '../pilotPanel';
import { type FlightState, type FlightVec, flightNose, flightSpeed } from '../sim/flight';
import type { DvCorrection } from '../sim/dvCorrection';
import {
  createProgradeMarkers, type LabelHold, type MarkerBurn, type MarkerTarget,
} from './progradeMarkers';
import { TEST_SWITCHES } from '../urlFlags.ts';

/** What the target marker reads off the guidance the pilot panel shares with it. */
export interface ProgradeGuidance {
  label: string;
  dir: FlightVec | null;
  radiusM: number;
  rangeM: number;
}

export interface ProgradeHudDeps {
  camera: THREE.PerspectiveCamera;
  scene: THREE.Scene;
  /** The group the markers hang in, and the inverse rotation carrying a velocity into it. */
  bubble: THREE.Group;
  _invBubble: THREE.Quaternion;
  flight: FlightState;
  guidanceToTarget: () => ProgradeGuidance | null;
  assistLive: () => boolean;
  /** NAMES is drawing the picked body's name this frame, so the marker carries its range alone. */
  targetNamed?: () => boolean;
  /** Live reads: main.ts still owns these. */
  readonly engagement: object | null;
  readonly dvCorrection: DvCorrection;
}

export interface ProgradeHudHandle {
  /** Repaint the velocity, target, assist-aim and burn markers for this frame. */
  update(): void;
  dispose(): void;
}

export function createProgradeHud(deps: ProgradeHudDeps): ProgradeHudHandle {
  const { camera, scene, bubble, _invBubble, flight, guidanceToTarget, assistLive } = deps;
  // ---------- Where the ship is going ----------
  // `?progradenose=1` pins both velocity markers to the nose instead of the
  // velocity, `?progradeoff=1` draws neither, `?hudinsetnone=1` clamps on the
  // bare margin rect with the HUD panels ignored, `?targetmarkoff=1` draws no
  // target marker, `?targetmarkalways=1` skips both of its suppression rules
  // and `?assistaimnose=1` pins the assist-aim marker to the nose instead of
  // the assist's own commanded direction: the broken-on-purpose variants
  // `tools/preview/descent-dojo.mjs --prograde --inject prograde-nose`,
  // `--inject prograde-none`, `--inject hud-inset-none` and `--targetmark
  // --inject targetmark-none`, `--inject targetmark-always`, and
  // `pilot-relative-check.mjs --inject aim-marker-on-nose` open. Nothing a
  // player reaches sets any of them. In DEV only, `?labelnohold=1` picks each
  // label's side afresh every frame and `?labelfrozen=1` never changes it:
  // `label-side-check.mjs --inject no-hold` and `--inject frozen`.
  const PROGRADE_NOSE = TEST_SWITCHES && new URLSearchParams(location.search).get('progradenose') === '1';
  const PROGRADE_OFF = TEST_SWITCHES && new URLSearchParams(location.search).get('progradeoff') === '1';
  const HUD_INSET_NONE = TEST_SWITCHES && new URLSearchParams(location.search).get('hudinsetnone') === '1';
  const TARGET_MARK_OFF = TEST_SWITCHES && new URLSearchParams(location.search).get('targetmarkoff') === '1';
  const TARGET_MARK_ALWAYS = TEST_SWITCHES && new URLSearchParams(location.search).get('targetmarkalways') === '1';
  const ASSIST_AIM_NOSE = TEST_SWITCHES && new URLSearchParams(location.search).get('assistaimnose') === '1';
  // `?targetnamedtwice=1` keeps the name in the target label while NAMES draws
  // it too: `descent-dojo.mjs --targetmark --inject targetmark-named-twice`.
  const TARGET_NAMED_TWICE = TEST_SWITCHES && new URLSearchParams(location.search).get('targetnamedtwice') === '1';
  const labelParams = new URLSearchParams(location.search);
  const LABEL_HOLD: LabelHold = !TEST_SWITCHES ? 'held'
    : labelParams.get('labelnohold') === '1' ? 'frame'
      : labelParams.get('labelfrozen') === '1' ? 'frozen' : 'held';
  const progradeMarkers = createProgradeMarkers({
    camera,
    // The flight state's own frame is the bubble's parent's, so the bubble's
    // inverse is what carries a velocity into the frame the markers hang in.
    toMount: () => _invBubble,
    suppressed: PROGRADE_OFF,
    ignorePanels: HUD_INSET_NONE,
    targetSuppressed: TARGET_MARK_OFF,
    targetAlways: TARGET_MARK_ALWAYS,
    labelHold: LABEL_HOLD,
  });
  bubble.add(progradeMarkers.group);
  scene.userData['simPrograde'] = { read: () => progradeMarkers.read() };
  const _progradeVel = new THREE.Vector3();
  const _progradeNose: FlightVec = { x: 0, y: 0, z: 0 };
  const _targetMarkDir = new THREE.Vector3();
  const _burnMarkDir = new THREE.Vector3();
  const _assistAimVec = new THREE.Vector3();
  const _assistAimNoseTmp: FlightVec = { x: 0, y: 0, z: 0 };

  /**
   * The picked body's own marker, or null when nothing is picked. The label is
   * composed here and never in the marker module: it carries the body's name
   * and its range through `formatDistanceForHud`, the SAME formatter the pilot
   * panel's own DISTANCE row uses, so the two can never disagree about a range.
   */
  function targetMarkerArg(): MarkerTarget | null {
    const guide = guidanceToTarget();
    if (guide === null || guide.dir === null) return null;
    const range = formatDistanceForHud(guide.rangeM);
    const named = !TARGET_NAMED_TWICE && deps.targetNamed?.() === true;
    return {
      dir: _targetMarkDir.set(guide.dir.x, guide.dir.y, guide.dir.z),
      rangeM: guide.rangeM,
      radiusM: guide.radiusM,
      label: named ? range : `${guide.label} ${range}`,
    };
  }

  /** The assist's own commanded direction, or null while it is off or holds none. */
  function assistAimArg(): THREE.Vector3 | null {
    if (!assistLive()) return null;
    if (ASSIST_AIM_NOSE) {
      const n = flightNose(flight, _assistAimNoseTmp);
      return _assistAimVec.set(n.x, n.y, n.z);
    }
    const aim = flight.assistAim;
    return aim === null ? null : _assistAimVec.set(aim.x, aim.y, aim.z);
  }

  /**
   * Where the delta-v map's armed plan wants the nose, or null while it wants it
   * nowhere. The direction is heliocentric and the flight frame differs from
   * heliocentric by a velocity and not by a rotation, so it goes to the marker as
   * it comes off the map.
   */
  function burnMarkerArg(): MarkerBurn | null {
    const mark = deps.dvCorrection.state().marker;
    if (mark === null) return null;
    return {
      dir: _burnMarkDir.set(mark.dir.x, mark.dir.y, mark.dir.z),
      kind: mark.kind,
      label: mark.label,
    };
  }

  /** Cruise only: inside a fight nothing steps the flight state, so its velocity is stale. */
  function updateProgradeMarkers(): void {
    if (deps.engagement !== null) {
      progradeMarkers.update(_progradeVel.set(0, 0, 0), null, null, null);
      return;
    }
    // A resting ship gets neither marker: the target one would name the ground
    // under the hull at nought metres and the assist holds no aim, and at the
    // attitude a landing leaves both project down onto the bottom action bar.
    if (flight.grounded !== null) {
      progradeMarkers.update(_progradeVel.set(0, 0, 0), null, null);
      return;
    }
    const target = targetMarkerArg();
    const burn = burnMarkerArg();
    if (PROGRADE_NOSE) {
      const n = flightNose(flight, _progradeNose);
      progradeMarkers.update(
        _progradeVel.set(n.x, n.y, n.z).multiplyScalar(flightSpeed(flight)), target,
        assistAimArg(), burn);
      return;
    }
    const v = flight.velocity;
    progradeMarkers.update(_progradeVel.set(v.x, v.y, v.z), target, assistAimArg(), burn);
  }

  return {
    update: updateProgradeMarkers,
    dispose: () => {
      bubble.remove(progradeMarkers.group);
      progradeMarkers.dispose();
      delete scene.userData['simPrograde'];
    },
  };
}
